import { type FormEvent, useState } from 'react';
import { i18n } from '@wms/shared';
import { api, ApiError } from '../lib/api.js';
import { Dialog } from './Dialog.js';
import { NumberInput } from './NumberInput.js';
import { SkuCodeInput, SkuNameInput } from './SkuCodeInput.js';

const { t } = i18n;

/**
 * Jonas #2 (2026-06-18): quickly turn a single COMPONENT into a sellable
 * PRODUCT ("Prekė"). Pre-fills the name from the component; the new product's
 * BOM is just this component at the entered quantity (default 1).
 */
export function CreateProductFromComponentDialog({
  partnerId,
  component,
  onClose,
  onCreated,
}: {
  partnerId: string;
  component: { id: string; code: string; nameLt: string; nameEn: string | null };
  onClose: () => void;
  onCreated: (productId: string) => void;
}) {
  const [code, setCode] = useState('');
  const [nameLt, setNameLt] = useState(component.nameLt);
  const [nameEn, setNameEn] = useState(component.nameEn ?? '');
  const [qty, setQty] = useState<number | null>(1);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const product = await api<{ id: string }>(
        `/partners/${partnerId}/skus/${component.id}/as-product`,
        {
          method: 'POST',
          body: {
            code: code.trim(),
            nameLt,
            nameEn: nameEn.trim() ? nameEn : undefined,
            qtyRequired: qty && qty > 0 ? Math.trunc(qty) : 1,
          },
        },
      );
      onCreated(product.id);
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'sku_code_taken'
          ? t('sku.code_taken')
          : t('common.error'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t('sku.from_component.title')} onClose={onClose}>
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">
          {t('sku.from_component.hint')} <span className="font-mono">{component.code}</span> —{' '}
          {component.nameLt}
        </p>
        <label className="block space-y-1">
          <span className="font-medium">{t('sku.col.code')}</span>
          <SkuCodeInput
            partnerId={partnerId}
            value={code}
            onChange={setCode}
            required
            className="w-full rounded border border-border bg-background px-2 py-1.5 font-mono"
          />
        </label>
        <label className="block space-y-1">
          <span className="font-medium">{t('sku.col.name_lt')}</span>
          <SkuNameInput
            partnerId={partnerId}
            value={nameLt}
            onChange={setNameLt}
            required
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>
        <label className="block space-y-1">
          <span className="font-medium">{t('sku.col.name_en')}</span>
          <input
            value={nameEn}
            onChange={(e) => setNameEn(e.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>
        <label className="block space-y-1">
          <span className="font-medium">{t('sku.from_component.qty')}</span>
          <NumberInput
            integer
            min={1}
            value={qty}
            onChange={setQty}
            className="w-32 rounded border border-border bg-background px-2 py-1.5 tabular-nums"
          />
        </label>

        {error ? <div className="text-red-600">{error}</div> : null}

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy || !code.trim() || !nameLt.trim()}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('common.create')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
